import React, { useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
// TODO: Import via props or create satelliteApi wrapper
// import MissionService from '../../../../service/MissionService';
import { getSatelliteGroups, getAllSatelliteGroupOrbit, cloneMissionSimulationGroup, deleteMissionSimulationGroup, deleteMissionSimulationGroups } from '../../../../service/satelliteApi';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import { getReportUsedEntities } from '../../../../lib/accessAnalysisReports';
import { applyOrbitReportUpdatesToTreeItems, mapApiSatToTreeItem } from '../../../../lib/missionSimulationMapper';
import { TreeSection, SatIcon, type SortOption } from '../../Common';
import TreeItem, { TreeItemContext } from './TreeItem';
import TreeGroup from './TreeGroup';
import type { SatGroup } from '../../../../types/mission';
import type { MissionStore } from '../../../../../../../store/MissionSlice';
import { showToastMessage } from '../../../../../../../utils/commonFunctions';
import styles from '../../LeftSideBar.module.scss';
import ConfirmationDialog from '../../../../../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import { SatelliteGroupIcon, TleIcon } from '../../../../../../UnifiedControlPlane/assets/icons/Icons';

interface SatelliteSectionProps {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  disabled?: boolean;
}

const isMultiSelectClick = (event: { metaKey: boolean; ctrlKey: boolean }) => event.metaKey || event.ctrlKey;

const toggleSelectedId = (ids: string[], id: string) => (
  ids.includes(id) ? ids.filter(existing => existing !== id) : [...ids, id]
);

function sortItems<T extends { name: string; id: string; createdAt?: string | number | null }>(items: T[], sort: SortOption): T[] {
  return [...items].sort((a, b) => {
    if (sort === 'az') return a.name.localeCompare(b.name);
    if (sort === 'za') return b.name.localeCompare(a.name);
    const aT = a.createdAt ? new Date(a.createdAt).getTime() : (parseInt(a.id.replace(/\D/g, '').slice(0, 13), 10) || 0);
    const bT = b.createdAt ? new Date(b.createdAt).getTime() : (parseInt(b.id.replace(/\D/g, '').slice(0, 13), 10) || 0);
    return sort === 'newest' ? bT - aT : aT - bT;
  });
}

const SatelliteSection: React.FC<SatelliteSectionProps> = ({ collapsed, onToggleCollapsed, disabled = false }) => {
  const {
    satellites, selectedItemId, openAddSat, selectTreeItem, toggleSatActive, toggleSatGroupExpand,
    openSatelliteOrbitEdit, openGroupOrbitEdit, analyserOpen, coverageAnalyserOpen, reportHoverHighlights, shouldBlockUI, setSatellites,
    selectedReportDetail, selectedCoverageReportDetail, invalidateAccessWindows, addSatModalOpen,
  } = useMDCockpit();
  const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);
  const cesium = useCesium();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();

  const currentSatCount = satellites.reduce((n, item) =>
    (item as SatGroup).type === 'group' ? n + (item as SatGroup).children.length : n + 1, 0);

  const handleAddSatellite = useCallback(() => {
    const maxSatellites = userData?.['feature-allowed']?.['missionModelMaxSatellites'] || 0;
    if (maxSatellites > 0 && currentSatCount >= maxSatellites) {
      showToastMessage('error', `You have reached the maximum limit of ${maxSatellites} satellites. Please delete an existing satellite to add a new one.`);
      return;
    }

    openAddSat();
  }, [userData, currentSatCount, openAddSat]);

  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortOption>('newest');
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [deletingGroups, setDeletingGroups] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; name: string } | null>(null);
  const [deletingPending, setDeletingPending] = useState(false);
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false);

  React.useEffect(() => {
    if (analyserOpen || coverageAnalyserOpen) {
      setSelectedGroupIds([]);
    }
  }, [analyserOpen, coverageAnalyserOpen]);

  const hoverSatelliteIds = useMemo(() => new Set(reportHoverHighlights.satelliteIds), [reportHoverHighlights.satelliteIds]);
  const hoverGroupIds = useMemo(() => new Set(reportHoverHighlights.groupIds), [reportHoverHighlights.groupIds]);

  const activeReport = selectedReportDetail || selectedCoverageReportDetail;
  const usedEntities = useMemo(() => getReportUsedEntities(activeReport, satellites), [activeReport, satellites]);
  const isReportActive = activeReport !== null;

  const totalSats = satellites.reduce((n, item) =>
    (item as SatGroup).type === 'group' ? n + (item as SatGroup).children.length : n + 1, 0);

  const filteredSats = useMemo(() => {
    const q = query.toLowerCase();
    const list = q ? satellites.filter(item => {
      if ((item as SatGroup).type === 'group') {
        return (item as SatGroup).name.toLowerCase().includes(q);
      }
      return (item as any).name.toLowerCase().includes(q);
    }) : satellites;
    return sortItems(list, sort);
  }, [satellites, query, sort]);

  const refreshSatellites = useCallback(async () => {
    if (!mission_id || !scenario_id) return;
    const [satRes, orbitRes] = await Promise.allSettled([
      getSatelliteGroups(mission_id, scenario_id),
      getAllSatelliteGroupOrbit(mission_id, scenario_id),
    ]);
    if (satRes.status !== 'fulfilled') return;

    const list: any[] = satRes.value?.data?.data ?? satRes.value?.data ?? [];
    const reports: any[] = orbitRes.status === 'fulfilled'
      ? orbitRes.value?.data?.data ?? orbitRes.value?.data ?? []
      : [];
    const mapped = Array.isArray(list) ? list.map(mapApiSatToTreeItem) : [];
    setSatellites(Array.isArray(reports) && reports.length > 0
      ? applyOrbitReportUpdatesToTreeItems(mapped, reports)
      : mapped);
  }, [mission_id, scenario_id, setSatellites]);

  const duplicateSatelliteGroup = useCallback(async (groupId: string) => {
    if (!mission_id || !scenario_id) return;
    await cloneMissionSimulationGroup(mission_id, scenario_id, groupId);
    await refreshSatellites();
  }, [mission_id, scenario_id, refreshSatellites]);

  const deleteSatelliteGroup = useCallback(async (groupId: string) => {
    if (!mission_id || !scenario_id) return;
    const target = satellites.find(item => {
      if ((item as SatGroup).type === 'group') return (item as SatGroup).id === groupId;
      const sat = item as any;
      return sat.simulationGroupId === groupId;
    });
    const deletedIds = new Set<string>();
    if (target) {
      if ((target as SatGroup).type === 'group') {
        deletedIds.add((target as SatGroup).id);
        (target as SatGroup).children.forEach(child => deletedIds.add(child.id));
      } else {
        const sat = target as any;
        deletedIds.add(sat.id);
        if (sat.simulationGroupId) deletedIds.add(sat.simulationGroupId);
      }
    } else {
      deletedIds.add(groupId);
    }
    const res: any = await deleteMissionSimulationGroup(mission_id, scenario_id, groupId);
    invalidateAccessWindows();
    await refreshSatellites();
    setSelectedGroupIds(prev => prev.filter(id => id !== groupId));
    if (selectedItemId && deletedIds.has(selectedItemId)) selectTreeItem(null, null);
    cesium.highlightSatellite(null);
    cesium.setSelectedSatellite(null);

    const successMsg = res?.data?.message || res?.message || 'Satellite/Group deleted successfully';
    showToastMessage('success', successMsg);
  }, [mission_id, scenario_id, satellites, selectedItemId, selectTreeItem, refreshSatellites, invalidateAccessWindows, cesium]);

  const confirmPendingDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeletingPending(true);
    try {
      await deleteSatelliteGroup(pendingDelete.id);
    } catch (e: any) {
      console.error('[Satellite] Delete failed', e);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || 'Failed to delete Satellite/Group';
      showToastMessage('error', errorMsg);
    } finally {
      setPendingDelete(null);
      setDeletingPending(false);
    }
  }, [pendingDelete, deleteSatelliteGroup]);

  const handleDeleteSelectedGroups = useCallback(async () => {
    if (!mission_id || !scenario_id || selectedGroupIds.length === 0) return;
    const idsToDelete = selectedGroupIds;
    setDeletingGroups(true);
    try {
      const res: any = await deleteMissionSimulationGroups(mission_id, scenario_id, idsToDelete);
      invalidateAccessWindows();
      await refreshSatellites();
      setSelectedGroupIds([]);
      if (selectedItemId) selectTreeItem(null, null);
      cesium.highlightSatellite(null);
      cesium.setSelectedSatellite(null);

      const successMsg = res?.data?.message || res?.message || 'Selected Satellites/Groups deleted successfully';
      showToastMessage('success', successMsg);
    } catch (e: any) {
      console.error('[Satellite] Bulk delete failed', e);
      const errorMsg = e?.response?.data?.message || e?.response?.data?.Message || e?.data?.message || e?.message || 'Failed to delete selected Satellites/Groups';
      showToastMessage('error', errorMsg);
    } finally {
      setConfirmingBulkDelete(false);
      setDeletingGroups(false);
    }
  }, [mission_id, scenario_id, selectedGroupIds, selectedItemId, selectTreeItem, refreshSatellites, invalidateAccessWindows, cesium]);

  return (
    <>
      <TreeItemContext.Provider value={{ openMenuId, setOpenMenuId }}>
        <TreeSection
          title="Satellites"
          count={totalSats}
          onAdd={handleAddSatellite}
          query={query}
          onQuery={setQuery}
          sort={sort}
          onSort={setSort}
          empty={filteredSats.length === 0}
          emptyText="No satellites yet"
          collapsed={collapsed}
          onToggleCollapsed={onToggleCollapsed}
          disabled={disabled || shouldBlockUI}
          modalOpen={addSatModalOpen}
        >
          {filteredSats.map(item => {
            if ((item as SatGroup).type === 'group') {
              const grp = item as SatGroup;
              const groupSelected = selectedGroupIds.includes(grp.id);
              const groupHovered = hoverGroupIds.has(grp.id);
              const hoveredChildIds = grp.children.filter(child => hoverSatelliteIds.has(child.id)).map(child => child.id);
              const highlightedChildIds = groupSelected || groupHovered
                ? grp.children.map(c => c.id)
                : hoveredChildIds;
              const launched = grp?.children.every(sat => sat.tleMeta?.source === "custom_tle")
              const icon = launched ? <TleIcon /> : <SatelliteGroupIcon />
              return (
                <TreeGroup
                  key={grp.id}
                  id={grp.id}
                  name={grp.name}
                  isTleGroup={launched}
                  icon={icon}
                  dot={grp.dot}
                  expanded={grp.expanded}
                  active={grp.active}
                  children={grp.children}
                  selectedId={selectedItemId}
                  selected={selectedItemId === grp.id || groupSelected || groupHovered || hoveredChildIds.length > 0}
                  selectedChildIds={highlightedChildIds}
                  orbitStatus={grp.orbitPointStatus}
                  orbitColour={grp.orbitColour}
                  onSelectGroup={(event) => {
                    if (isMultiSelectClick(event)) {
                      setSelectedGroupIds(prev => toggleSelectedId(prev, grp.id));
                      return;
                    }
                    if (selectedItemId === grp.id || (selectedGroupIds.length === 1 && selectedGroupIds[0] === grp.id)) {
                      setSelectedGroupIds([]);
                      selectTreeItem(null, null);
                      cesium.highlightSatellite(null);
                      return;
                    }
                    setSelectedGroupIds([]);
                    selectTreeItem(grp.id, 'group');
                    cesium.highlightSatellite(grp.children[0]?.id ?? null);
                  }}
                  onToggleExpand={() => toggleSatGroupExpand(grp.id)}
                  onToggleActive={() => {
                    toggleSatActive(grp.id);
                    cesium.setGroupOrbitVisible(grp.children.map(c => c.id), !grp.active);
                  }}
                  onSelectChild={(id, event) => {
                    if (isMultiSelectClick(event)) {
                      setSelectedGroupIds(prev => toggleSelectedId(prev, grp.id));
                      return;
                    }
                    if (selectedItemId === id || (selectedGroupIds.length === 1 && selectedGroupIds[0] === grp.id)) {
                      setSelectedGroupIds([]);
                      selectTreeItem(null, null);
                      cesium.highlightSatellite(null);
                      return;
                    }
                    setSelectedGroupIds([]);
                    selectTreeItem(id, 'satellite');
                    cesium.highlightSatellite(id);
                  }}
                  onToggleChildActive={id => {
                    const child = grp.children.find(c => c.id === id);
                    if (child) { toggleSatActive(id); cesium.setSatOrbitVisible(child.id, !child.active); }
                  }}
                  onDuplicateGroup={() => duplicateSatelliteGroup(grp.id)}
                  onDeleteGroup={() => deleteSatelliteGroup(grp.id)}
                  onRequestDeleteGroup={() => setPendingDelete({ id: grp.id, name: grp.name })}
                  onEditGroup={() => { cesium.setClockPlaying(false); openGroupOrbitEdit(grp.id); }}
                  onEditChild={(id) => {
                    cesium.setClockPlaying(false);
                    openSatelliteOrbitEdit(id);
                  }}
                  onHoverChild={(id) => cesium.highlightSatellite(id)}
                  onHoverGroup={(id) => cesium.highlightSatellite(id ? grp.children.map(c => c.id) : null)}
                  disabled={shouldBlockUI}
                  unusedInReport={isReportActive && !usedEntities.groupIds.has(grp.id) && !grp.children.some(c => usedEntities.satelliteIds.has(c.id))}
                  unusedChildIds={isReportActive ? grp.children.filter(c => !usedEntities.satelliteIds.has(c.id)).map(c => c.id) : []}
                />
              );
            }
            const sat = item as any;
            const groupId = sat.simulationGroupId;
            const satHovered = hoverSatelliteIds.has(sat.id) || (!!groupId && hoverGroupIds.has(groupId));
            return (
              <TreeItem
                key={sat.id}
                id={sat.id}
                name={sat.name}
                dot={sat.dot}
                active={sat.active}
                icon={<SatIcon />}
                selected={selectedItemId === sat.id || satHovered || (!!groupId && selectedGroupIds.includes(groupId))}
                orbitStatus={sat.orbitPointStatus}
                orbitColour={sat.orbitColour}
                onSelect={(event) => {
                  if (isMultiSelectClick(event) && groupId) {
                    setSelectedGroupIds(prev => toggleSelectedId(prev, groupId));
                    return;
                  }
                  if (selectedItemId === sat.id || (!!groupId && selectedGroupIds.length === 1 && selectedGroupIds[0] === groupId)) {
                    setSelectedGroupIds([]);
                    selectTreeItem(null, null);
                    cesium.highlightSatellite(null);
                    return;
                  }
                  setSelectedGroupIds([]);
                  selectTreeItem(sat.id, 'satellite');
                  cesium.highlightSatellite(sat.id);
                }}
                onToggleActive={() => { toggleSatActive(sat.id); cesium.setSatOrbitVisible(sat.id, !sat.active); }}
                onEdit={() => { cesium.setClockPlaying(false); openSatelliteOrbitEdit(sat.id); }}
                onDuplicate={groupId ? () => duplicateSatelliteGroup(groupId) : undefined}
                onDelete={groupId ? () => deleteSatelliteGroup(groupId) : undefined}
                onRequestDelete={groupId ? () => setPendingDelete({ id: groupId, name: sat.name }) : undefined}
                onHover={(id) => cesium.highlightSatellite(id)}
                disabled={shouldBlockUI}
                unusedInReport={isReportActive && !usedEntities.satelliteIds.has(sat.id)}
              />
            );
          })}
        </TreeSection>
      </TreeItemContext.Provider>

      {ReactDOM.createPortal(
        <>
          <ConfirmationDialog
            type="delete"
            open={pendingDelete !== null}
            title="Delete Satellite?"
            loading={deletingPending}
            description={
              pendingDelete
                ? `Deleting ${pendingDelete.name} will permanently remove it from the database, and it cannot be restored once deleted.`
                : ''
            }
            confirm_button_text="Delete"
            onConfirm={confirmPendingDelete}
            onCancel={() => setPendingDelete(null)}
          />

          <ConfirmationDialog
            type="delete"
            open={confirmingBulkDelete}
            title="Delete Satellites/Groups?"
            loading={deletingGroups}
            description={`Deleting selected satellites/groups will permanently remove them from the database, and they cannot be restored once deleted.`}
            confirm_button_text="Delete"
            onConfirm={handleDeleteSelectedGroups}
            onCancel={() => setConfirmingBulkDelete(false)}
          />
        </>,
        document.body
      )}

      <div className={styles.segmentDivider} />
    </>
  );
};

export default SatelliteSection;
